import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaHome,
  FaUsers,
  FaProjectDiagram,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaChartBar,
  FaCog,
  FaEnvelope,
  FaUser,
  FaEdit
} from 'react-icons/fa';
import { IconType } from 'react-icons';
import { logout } from '../redux/slices/authSlice';
import { RootState } from '../redux/store';

// Wrapper component for dynamic icons
const IconWrapper: React.FC<{ icon: IconType; className?: string }> = ({ icon: Icon, className }) => {
  return <Icon className={className} /> as any;
};

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Close sidebar on route change (only on mobile)
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Ensure sidebar is open on large screens
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      }
    };

    handleResize(); // Call immediately
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.user-dropdown')) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: FaHome as IconType },
    { name: 'Users', href: '/users', icon: FaUsers as IconType },
    { name: 'Projects', href: '/projects', icon: FaProjectDiagram as IconType },
    { name: 'Landing Submissions', href: '/landing-submissions', icon: FaEdit as IconType },
  ];

  const handleLogout = () => {
    dispatch(logout()); // This already clears localStorage and cookies
    navigate('/login');
  };

  const isActive = (href: string) => {
    return location.pathname === href;
  };

  const getCurrentPageTitle = () => {
    const currentPage = navigation.find(item => isActive(item.href));
    return currentPage?.name || 'Dashboard';
  };

  const getBreadcrumbs = () => {
    const pathnames = location.pathname.split('/').filter(x => x);
    const breadcrumbs = [{ name: 'Home', href: '/' }];

    // Inject parent breadcrumb when navigating to a project from the users section
    if ((location.state as any)?.from === 'users' && pathnames[0] === 'projects') {
      breadcrumbs.push({ name: 'Users', href: '/users' });
    }

    pathnames.forEach((name, index) => {
      const href = `/${pathnames.slice(0, index + 1).join('/')}`;
      const navItem = navigation.find(item => item.href === href);
      if (navItem) {
        breadcrumbs.push({ name: navItem.name, href });
      }
    });

    return breadcrumbs;
  };

  return (
    <div className="h-screen w-screen bg-gray-50 overflow-hidden flex">
      {/* Mobile sidebar overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 40,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <div
        className={`fixed lg:relative top-0 left-0 h-full w-64 bg-white shadow-xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center">
            <img src="/logo.svg" alt="FoundersMate Icon" className="h-8 w-auto mr-3" />
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-md text-white hover:bg-white hover:bg-opacity-20 transition-colors"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="mt-8 px-4 flex-1 overflow-y-auto">
          <div className="space-y-1">
            {navigation.map((item) => (
              <motion.div
                key={item.name}
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                <button
                  onClick={() => {
                    navigate(item.href);
                    // Only close sidebar on mobile devices
                    if (window.innerWidth < 1024) {
                      setSidebarOpen(false);
                    }
                  }}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${isActive(item.href)
                    ? 'bg-primary text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                >
                  <IconWrapper
                    icon={item.icon}
                    className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-white' : 'text-gray-400'
                      }`}
                  />
                  {item.name}
                </button>
              </motion.div>
            ))}
          </div>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex items-center mb-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white text-sm font-medium shadow-md">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
          >
            <FaSignOutAlt className="mr-3 h-4 w-4" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <FaBars className="h-5 w-5" />
              </button>
              <div className="ml-4 lg:ml-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  {getCurrentPageTitle()}
                </h2>
                <p className="text-sm text-gray-500 hidden sm:block">
                  Welcome back, {user?.name}
                </p>
              </div>
            </div>

            {/* Top bar actions */}
            {/* <div className="flex items-center">
              <img src="/icon.svg" alt="FoundersMate Icon" className="h-8 w-8" />
            </div> */}
          </div>

          {/* Breadcrumbs */}
          <div className="px-6 py-2 bg-gray-50 border-t border-gray-100">
            <nav className="flex" aria-label="Breadcrumb">
              <ol className="flex items-center space-x-2">
                {getBreadcrumbs().map((breadcrumb, index) => (
                  <li key={breadcrumb.href} className="flex items-center">
                    {index > 0 && (
                      <svg className="w-4 h-4 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    <button
                      onClick={() => navigate(breadcrumb.href)}
                      className={`text-sm font-medium transition-colors ${index === getBreadcrumbs().length - 1
                        ? 'text-primary cursor-default'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      {breadcrumb.name}
                    </button>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <div className="max-w-full h-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 